import React, { useState, useRef, useEffect } from 'react';
import { Plus, Check } from 'lucide-react';
import { useTheme } from './ThemeContext';

/**
 * MoreFiltersPicker — „Mehr Filter": Popover mit Checkboxen, mit denen der Nutzer
 * auswählt, welche Filter-Chips sichtbar sind. Generisch + wiederverwendbar.
 *
 * @param {Array<{key:string,label:string}>} filters  alle wählbaren Filter
 * @param {string[]} visibleKeys                       aktuell sichtbar
 * @param {(next:string[])=>void} onChange
 * @param {string} addLabel                            Button-Text (z. B. „Mehr Filter")
 * @param {string} [title]                             Popover-Titel
 */
const MoreFiltersPicker = ({ filters, visibleKeys, onChange, addLabel, title }) => {
  const { isDarkMode } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const toggle = (key) => onChange(visibleKeys.includes(key) ? visibleKeys.filter((k) => k !== key) : [...visibleKeys, key]);

  const btnCls = isDarkMode
    ? 'inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-dashed border-white/25 text-white/60 hover:border-brand/50 hover:text-brand text-xs font-medium transition-colors cursor-pointer'
    : 'inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-dashed border-slate-300 text-slate-500 hover:border-brand/50 hover:text-brand text-xs font-medium transition-colors cursor-pointer';

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} className={btnCls}>
        <Plus className="w-3.5 h-3.5" /> {addLabel}
      </button>
      {open && (
        <div className={`absolute z-40 top-full left-0 mt-1 w-60 rounded-xl border shadow-lg overflow-hidden ${isDarkMode ? 'bg-gray-900 border-white/15' : 'bg-white border-slate-200'}`}>
          {title && <div className={`px-3 pt-2.5 pb-1 text-xs font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{title}</div>}
          <div className="max-h-72 overflow-y-auto px-1.5 py-1.5">
            {filters.map((f) => {
              const on = visibleKeys.includes(f.key);
              return (
                <button key={f.key} type="button" onClick={() => toggle(f.key)}
                  className={`flex items-center gap-2 w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${on ? (isDarkMode ? 'bg-brand/20 text-brand' : 'bg-brand/10 text-brand') : (isDarkMode ? 'text-white/70 hover:bg-white/5' : 'text-slate-600 hover:bg-slate-50')}`}>
                  <span className={`w-3.5 h-3.5 flex-shrink-0 rounded border flex items-center justify-center ${on ? 'bg-brand border-brand' : (isDarkMode ? 'border-white/30' : 'border-slate-300')}`}>
                    {on && <Check className="w-2.5 h-2.5 text-white" />}
                  </span>
                  <span className="flex-1 truncate">{f.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MoreFiltersPicker;
